import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {Sponsorship} from "types/Sponsorship";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DeleteSponsorship from "pages/Sponsorship/Delete";
import {toTitleCase} from 'services/string';
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import {formattedDate} from "services/date";
import DescriptionListItem from "components/DescriptionListItem";
import Chip from "components/Chip";
import SponsorableType from "types/SponsorableType";
import {Element, ElementPreview} from "types/Element";
import {useOperator} from "context/OperatorContext";
import {ExhibitPreview} from "../../../types/Exhibit";
import {TrailPreview} from "../../../types/Trail";

// Component ----

function ViewSponsorship() {
  const {id} = useParams()
  const {data: sponsorship, isLoading} = useQuery(
    ['sponsorship', {id}],
    () => ngage.sponsorships.get(parseInt(id as string))
  )

  const [deletingSponsorship, setDeletingSponsorship] = useState<Sponsorship | null>(null);

  const navigate = useNavigate()
  const {termFor} = useOperator()

  // Prepare action handlers
  function handleDeleteSponsorship() {
    if (sponsorship) setDeletingSponsorship(sponsorship)
  }

  function handleEditSponsorship() {
    if (id) navigate(`/sponsorships/${id}/edit`)
  }

  // Prepare page actions
  const actions = <>
    <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteSponsorship}>Delete</Button>
    <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditSponsorship}>Edit</Button>
  </>

  // Prepare breadcrumbs
  const breadcrumbs = {
    [termFor('Sponsorship')]: "/",
    [termFor('Sponsorships')]: "/sponsorships",
    [sponsorship?.name || `View ${termFor('Sponsorship')}`]: `/sponsorships/${id}`
  }

  return (
    <Page
      title={sponsorship ? `${termFor('Sponsorship')} ${sponsorship.reference}` : `View ${termFor('Sponsorship')}`}
      breadcrumbs={breadcrumbs}
      actions={actions}
      isLoading={isLoading}
    >
      {!!sponsorship &&
        <section>
          <Card
            heading={sponsorship.name}
            subheading={`${toTitleCase(sponsorship.tier.name)} Tier`}
          >
            <DescriptionList>

              <DescriptionListItem span={1}>
                <DescriptionTerm>Reference</DescriptionTerm>
                <DescriptionDetails>
                  {sponsorship.reference}
                </DescriptionDetails>
              </DescriptionListItem>

              <DescriptionListItem span={1}>
                <DescriptionTerm>Amount Paid</DescriptionTerm>
                <DescriptionDetails>
                  {sponsorship.paid_amount_formatted}
                </DescriptionDetails>
              </DescriptionListItem>

              <DescriptionListItem span={1}>
                <DescriptionTerm>Starts At</DescriptionTerm>
                <DescriptionDetails>
                  {formattedDate(sponsorship.starts_at)}
                </DescriptionDetails>
              </DescriptionListItem>

              <DescriptionListItem span={1}>
                <DescriptionTerm>Ends At</DescriptionTerm>
                <DescriptionDetails>
                  {sponsorship.ends_at ? formattedDate(sponsorship.ends_at) : 'Never'}
                </DescriptionDetails>
              </DescriptionListItem>

              <DescriptionListItem span={1}>
                <DescriptionTerm>Tier</DescriptionTerm>
                <DescriptionDetails>
                  <Chip
                    name={sponsorship.tier.name}
                    description={`${sponsorship.tier.price_formatted} for ${sponsorship.tier.duration.toHuman()}`}
                    href={`/sponsorship-tiers/${sponsorship.tier.id}`}
                  />
                </DescriptionDetails>
              </DescriptionListItem>

              <DescriptionListItem span={1}>
                <DescriptionTerm>{termFor('Sponsor')}</DescriptionTerm>
                <DescriptionDetails>
                  <Chip
                    name={sponsorship.sponsor.name}
                    description={toTitleCase(sponsorship.sponsor_type)}
                    href={`/${sponsorship.sponsor_type}s/${sponsorship.sponsor.id}`}
                  />
                </DescriptionDetails>
              </DescriptionListItem>

              <DescriptionListItem span={1}>
                <DescriptionTerm>Entity</DescriptionTerm>
                <DescriptionDetails>
                  {sponsorship.sponsorable_type === SponsorableType.SITE &&
                    <Chip
                      name={sponsorship.sponsorable.name}
                      description={'Site'}
                      href={`/sites/${sponsorship.sponsorable.id}`}
                    />
                  }

                  {sponsorship.sponsorable_type === SponsorableType.ZONE &&
                    <Chip
                      name={sponsorship.sponsorable.name}
                      description={'Zone'}
                      href={`/zones/${sponsorship.sponsorable.id}`}
                    />
                  }

                  {sponsorship.sponsorable_type === SponsorableType.FACILITY &&
                    <Chip
                      name={sponsorship.sponsorable.name}
                      description={'Facility'}
                      href={`/facilities/${sponsorship.sponsorable.id}`}
                    />
                  }

                  {sponsorship.sponsorable_type === SponsorableType.EXHIBIT &&
                    <Chip
                      name={sponsorship.sponsorable.name}
                      description={'Exhibit'}
                      href={`/exhibits/${sponsorship.sponsorable.id}`}
                      backgroundSrc={(sponsorship.sponsorable as ExhibitPreview).image?.preview_url}
                    />
                  }

                  {sponsorship.sponsorable_type === SponsorableType.ELEMENT &&
                    <Chip
                      name={sponsorship.sponsorable.name}
                      description={'Exhibit Element'}
                      href={`/exhibits/${(sponsorship.sponsorable as Element).exhibit_id}`}
                      backgroundSrc={(sponsorship.sponsorable as ElementPreview).image?.preview_url}
                    />
                  }

                  {sponsorship.sponsorable_type === SponsorableType.TRAIL &&
                    <Chip
                      name={sponsorship.sponsorable.name}
                      description={'Trail'}
                      href={`/trails/${sponsorship.sponsorable.id}`}
                      backgroundSrc={(sponsorship.sponsorable as TrailPreview).image?.preview_url}
                    />
                  }
                </DescriptionDetails>
              </DescriptionListItem>

            </DescriptionList>
          </Card>

          {/*<div className="mt-4">*/}
          {/*    <StatList>*/}
          {/*        <StatBox name="Visits" color={StatBox.colors.BLUE} icon={PlayIcon} stat={3} />*/}
          {/*        <StatBox name="Visit Cost" color={StatBox.colors.PURPLE} icon={CashIcon} stat={`${sponsorship.currency.symbol}${((sponsorship.price.discounted / 3) / 100).toFixed(2)}`} />*/}
          {/*        <StatBox name="Vouchers Used" color={StatBox.colors.PINK} icon={GiftIcon} stat={5} />*/}
          {/*    </StatList>*/}
          {/*</div>*/}
        </section>
      }

      {/* Delete confirmation modal */}
      <DeleteSponsorship
        sponsorship={deletingSponsorship}
        show={sponsorship === deletingSponsorship}
        onCancel={() => setDeletingSponsorship(null)}
        onDelete={() => navigate('/sponsorships')}
        onDeleteFail={() => setDeletingSponsorship(null)}
      />
    </Page>
  );
}

export default ViewSponsorship;